import { IcShare } from '@assets/icons/24x24';
import { HStack, Icon, Text } from '@chakra-ui/react';
import CustomChipList from '@components/common/inputs/CustomChipList';
import { MessageInput } from '@components/ui/input/MessageInput';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole, selectActiveSite } from '@redux/authent/authent.selectors';
import { updateNewLogBookEntry } from '@redux/global/global.reducer';
import { selectAllLogs, selectNewLogBookEntry } from '@redux/global/global.selector';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { store } from '@redux/store';
import { useCreateInstructionMutation } from '@services/chat/chat.api';
import { ALL_OPTION_ID, stringToOption } from '@utils/components/customChipList.utils';
import { getLogAuthor, getLogContent } from '@utils/components/logbook.utils';
import { formatDate } from '@utils/date.utils';
import { debounce } from 'lodash';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FixedSizeList, ListOnScrollProps } from 'react-window';

import { RoleEnum } from '@/types/authent/roles.types';
import { HoloLog, LogTypeEnum } from '@/types/c2/logs.type';
import { DateTimeEnum } from '@/types/dateTime.types';

import CockpitPanelHeader from '../CockpitPanelHeader';
import Panel from '../Panel';
import LogbookList from './LogbookList';

export default function LogBook() {
  const { formatMessage } = useIntl();
  const activeSite = useAppSelector(selectActiveSite);
  const newLogbookEntry = useAppSelector(selectNewLogBookEntry);
  const csvDelimiter = ';';
  const [selectedFilters, setSelectedFilters] = useState<string[]>([ALL_OPTION_ID]);
  const [createInstruction] = useCreateInstructionMutation();
  const resetNewLogbookEntry = useWithDispatch(updateNewLogBookEntry);

  const canCreateInstruction = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_INSTRUCTION));

  const logs = useAppSelector(selectAllLogs);
  const filteredLogs = useMemo(
    () => logs.filter((log) => selectedFilters.includes(ALL_OPTION_ID) || selectedFilters.includes(log.type)),
    [logs, selectedFilters],
  );

  function convertLogsToCSV(logs: HoloLog[]) {
    const headers = [
      formatMessage({ id: 'components.logbook.csvHeader.date' }),
      formatMessage({ id: 'components.logbook.csvHeader.type' }),
      formatMessage({ id: 'components.logbook.csvHeader.author' }),
      formatMessage({ id: 'components.logbook.csvHeader.content' }),
    ];

    const csvRows = [
      `sep=${csvDelimiter}`,
      headers.join(csvDelimiter),
      ...logs.map((log) => {
        const author = getLogAuthor(log);
        const firstName = author?.firstName ? ` ${author.firstName}` : '';
        return [
          formatDate(log.time, DateTimeEnum.DATE_TIME),
          log.type,
          `${author?.name}${firstName}`,
          getLogContent(log),
        ].join(csvDelimiter);
      }),
    ];

    return csvRows.join('\n');
  }

  function downloadCSV(logs: HoloLog[]) {
    const csvContent = convertLogsToCSV(logs);

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const url = URL.createObjectURL(blob);

    const date = formatDate(selectSituationTime(store.getState()) ?? Date.now(), DateTimeEnum.DATE_TIME);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `filtered_logs_${date}.csv`);

    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  const [shouldForceScroll, setShouldForceScroll] = useState(true);
  const listRef = useRef<FixedSizeList | null>(null);

  const scrollToBottom = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(filteredLogs.length - 1, 'end');
    }
  }, [filteredLogs.length]);

  const handleScroll = debounce(({ scrollOffset, scrollUpdateWasRequested }: ListOnScrollProps) => {
    if (scrollUpdateWasRequested || !listRef.current) {
      return;
    }
    const isBottom = scrollOffset >= filteredLogs.length * 102 - Number(listRef.current.props.height) - 20;
    setShouldForceScroll(isBottom);
  }, 100);

  useLayoutEffect(() => {
    if (shouldForceScroll) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [filteredLogs, scrollToBottom, shouldForceScroll]);

  if (newLogbookEntry) {
    resetNewLogbookEntry(false);
  }

  return (
    <Panel
      width="60%"
      isEmpty={filteredLogs.length === 0}
      emptyLabel={formatMessage({ id: 'components.logbook.noLog' })}
      gap={0}
      header={<CockpitPanelHeader label={formatMessage({ id: 'components.logbook.title' })} hideCommonButton />}
      footer={
        canCreateInstruction ? (
          <MessageInput
            onSubmit={(instruction) => {
              createInstruction({ msg: instruction, site: activeSite?.code ?? '' });
              setShouldForceScroll(true);
            }}
          />
        ) : undefined
      }
      subHeader={
        <HStack width="100%" gap={3} paddingY={1} paddingX={2} backgroundColor="neutral.900" flexShrink={0}>
          <HStack gap={1}>
            <Text color={logs.length === 0 ? 'neutral.300' : 'neutral.white'}>
              <FormattedMessage id="components.panel.filter" />
            </Text>
            <CustomChipList
              availableOptions={Object.keys(LogTypeEnum).map(stringToOption)}
              selectedOptions={selectedFilters.map(stringToOption)}
              isDisabled={logs.length === 0}
              displayOptionAll
              path="components.logbook.filter"
              onChange={(value: string[]) => {
                setSelectedFilters(value);
                setShouldForceScroll(true);
              }}
            />
          </HStack>
          <Icon
            as={IcShare}
            color="neutral.300"
            cursor="pointer"
            width="24px"
            height="24px"
            marginLeft="auto"
            onClick={() => downloadCSV(filteredLogs)}
          />
        </HStack>
      }
    >
      <LogbookList logs={filteredLogs.map((log) => log.code)} listRef={listRef} handleScroll={handleScroll} />
    </Panel>
  );
}
